import Phaser from 'phaser';
import {
  CASH_APP_MODAL,
  COLORS,
  DEPTHS,
  FONTS,
  GAME_HEIGHT,
  GAME_WIDTH,
  toHexColor,
} from '../config/gameConfig';
import { COPY } from '../content/copy';
import type { AgingInvoice } from '../content/seed-invoices';
import type { CashApplicationAllocation, EventBus, PendingPayment } from '../systems/EventBus';
import type { ARLedger } from '../systems/ARLedger';
import { renderHelpLink } from './HelpLink';
import { propagateDepth } from './propagateDepth';

interface CustomerSummary {
  customer: string;
  openCount: number;
  outstanding: number;
}

const summarizeCustomers = (invoices: readonly AgingInvoice[]): CustomerSummary[] => {
  const map = new Map<string, CustomerSummary>();
  for (const inv of invoices) {
    const existing = map.get(inv.customer);
    if (existing) {
      existing.openCount += 1;
      existing.outstanding += inv.amount;
    } else {
      map.set(inv.customer, {
        customer: inv.customer,
        openCount: 1,
        outstanding: inv.amount,
      });
    }
  }
  return [...map.values()].sort((a, b) => a.customer.localeCompare(b.customer));
};

const invoicesForCustomer = (
  invoices: readonly AgingInvoice[],
  customer: string,
): readonly AgingInvoice[] =>
  invoices
    .filter((inv) => inv.customer === customer && inv.status !== 'disputed')
    .slice()
    .sort((a, b) => b.agingDays - a.agingDays);

const formatDollars = (n: number): string =>
  `$${n.toLocaleString('en-US', { maximumFractionDigits: 0 })}`;

export class CashApplicationModal {
  private container: Phaser.GameObjects.Container | null = null;
  private escHandler: ((ev: KeyboardEvent) => void) | null = null;
  private customers: readonly CustomerSummary[];
  private selectedCustomer: string | null = null;
  private allocations = new Map<string, number>();
  private currentPayment: PendingPayment | null = null;

  private readonly onOpenFor = (p: { payment: PendingPayment }) => {
    if (this.container) return;
    this.open(p.payment);
  };

  constructor(
    private readonly scene: Phaser.Scene,
    private readonly bus: EventBus,
    _tooltip?: import('./Tooltip').Tooltip,
    private readonly ledger?: ARLedger,
  ) {
    this.customers = summarizeCustomers(this.ledgerInvoices());
    bus.on('ar-ledger:changed', () => {
      this.customers = summarizeCustomers(this.ledgerInvoices());
    });
    bus.on('cash-application:open-for', this.onOpenFor);
    scene.events.once(Phaser.Scenes.Events.SHUTDOWN, () => this.teardown());
    scene.events.once(Phaser.Scenes.Events.DESTROY, () => this.teardown());
  }

  private ledgerInvoices(): readonly AgingInvoice[] {
    return this.ledger ? this.ledger.current : [];
  }

  confirmForTest(customer: string, allocations: readonly CashApplicationAllocation[]): void {
    this.selectedCustomer = customer;
    this.allocations = new Map(allocations.map((a) => [a.invoiceId, a.amount]));
    this.emitSent();
  }

  private open(payment: PendingPayment): void {
    this.currentPayment = payment;
    this.selectedCustomer = null;
    this.allocations.clear();
    this.bus.emit('term:unlocked', { term: 'cash-application' });
    this.bus.emit('cash-application:opened', { wireId: payment.id });
    this.render();
  }

  private emitSent(): void {
    if (!this.selectedCustomer || !this.currentPayment) return;
    const payment = this.currentPayment;
    const allocations: CashApplicationAllocation[] = [...this.allocations.entries()].map(
      ([invoiceId, amount]) => ({ invoiceId, amount }),
    );
    const applied = allocations.reduce((sum, a) => sum + a.amount, 0);
    this.bus.emit('cash-application:sent', {
      wireId: payment.id,
      customer: this.selectedCustomer,
      correctCustomer: this.selectedCustomer === payment.customer,
      allocations,
      unapplied: Math.max(0, payment.amount - applied),
    });
  }

  private render(): void {
    if (this.container) {
      this.container.destroy();
      this.container = null;
    }

    const x = Math.round((GAME_WIDTH - CASH_APP_MODAL.width) / 2);
    const y = Math.round((GAME_HEIGHT - CASH_APP_MODAL.height) / 2);

    const root = this.scene.add.container(0, 0);
    root.setDepth(DEPTHS.modal);

    const backdrop = this.scene.add
      .rectangle(0, 0, GAME_WIDTH, GAME_HEIGHT, COLORS.black, CASH_APP_MODAL.backdropAlpha)
      .setOrigin(0, 0);
    root.add(backdrop);

    const panel = this.scene.add
      .rectangle(x, y, CASH_APP_MODAL.width, CASH_APP_MODAL.height, COLORS.windowBody)
      .setOrigin(0, 0)
      .setStrokeStyle(1, COLORS.windowBorder)
      .setInteractive();
    root.add(panel);

    const payment = this.currentPayment;
    const titleStr = payment
      ? `Apply payment — Wire #${payment.id}`
      : COPY.cashApplication.title;
    const title = this.scene.add.text(
      x + CASH_APP_MODAL.paddingX,
      y + CASH_APP_MODAL.paddingY,
      titleStr,
      {
        fontFamily: FONTS.ui,
        fontSize: CASH_APP_MODAL.titleFontSize,
        color: toHexColor(COLORS.textPrimary),
        fontStyle: 'bold',
      },
    );
    root.add(title);

    renderHelpLink(
      this.scene,
      root,
      x + CASH_APP_MODAL.width - CASH_APP_MODAL.paddingX,
      y + CASH_APP_MODAL.paddingY + 4,
      'cash-application',
      this.bus,
    );

    const wireY = y + CASH_APP_MODAL.paddingY + title.height + 6;
    const wireStr = payment
      ? `$${payment.amount.toLocaleString('en-US')} received via wire transfer`
      : COPY.cashApplication.wireSummary;
    const wire = this.scene.add.text(
      x + CASH_APP_MODAL.paddingX,
      wireY,
      wireStr,
      {
        fontFamily: FONTS.ui,
        fontSize: CASH_APP_MODAL.wireSummaryFontSize,
        color: toHexColor(COLORS.accentWarn),
        fontStyle: 'bold',
      },
    );
    root.add(wire);

    const remitY = wireY + wire.height + 2;
    const remitStr = payment
      ? `Remittance reference: "${payment.remittance}"`
      : COPY.cashApplication.remittanceHint;
    const remit = this.scene.add.text(
      x + CASH_APP_MODAL.paddingX,
      remitY,
      remitStr,
      {
        fontFamily: FONTS.ui,
        fontSize: CASH_APP_MODAL.remittanceFontSize,
        color: toHexColor(COLORS.textMuted),
      },
    );
    root.add(remit);

    const contentY = remitY + remit.height + 12;

    if (this.selectedCustomer === null) {
      this.renderCustomerPicker(root, x, y, contentY);
    } else {
      this.renderAllocator(root, x, y, contentY);
    }

    this.escHandler = (ev: KeyboardEvent) => {
      if (ev.key === 'Escape') this.close();
    };
    this.scene.input.keyboard?.on('keydown', this.escHandler);

    this.container = root;
    propagateDepth(root, DEPTHS.modal);
  }

  private renderCustomerPicker(
    root: Phaser.GameObjects.Container,
    x: number,
    y: number,
    contentY: number,
  ): void {
    const prompt = this.scene.add.text(
      x + CASH_APP_MODAL.paddingX,
      contentY,
      COPY.cashApplication.customerPickerPrompt,
      {
        fontFamily: FONTS.ui,
        fontSize: CASH_APP_MODAL.promptFontSize,
        color: toHexColor(COLORS.textPrimary),
      },
    );
    root.add(prompt);

    // Lookup hint: tells the player to cross-check the remittance reference
    // against the AR aging spreadsheet to find the right customer.
    const hintY = contentY + prompt.height + 4;
    const hint = this.scene.add.text(
      x + CASH_APP_MODAL.paddingX,
      hintY,
      '↗ Cross-check the remittance against the AR aging spreadsheet — pick the customer whose open invoices match.',
      {
        fontFamily: FONTS.ui,
        fontSize: '11px',
        color: toHexColor(COLORS.accentWarn),
        fontStyle: 'italic',
        wordWrap: { width: CASH_APP_MODAL.width - CASH_APP_MODAL.paddingX * 2 },
      },
    );
    root.add(hint);

    let rowY = hintY + hint.height + 10;
    const rowWidth = CASH_APP_MODAL.width - CASH_APP_MODAL.paddingX * 2;
    const listHeight = y + CASH_APP_MODAL.height - CASH_APP_MODAL.paddingY - 40 - rowY;
    const maxVisible = Math.floor(
      listHeight / (CASH_APP_MODAL.customerRowHeight + CASH_APP_MODAL.customerRowGap),
    );
    const visible = this.customers.slice(0, Math.max(1, maxVisible));

    for (const summary of visible) {
      const row = this.scene.add
        .rectangle(
          x + CASH_APP_MODAL.paddingX,
          rowY,
          rowWidth,
          CASH_APP_MODAL.customerRowHeight,
          COLORS.windowChrome,
        )
        .setOrigin(0, 0)
        .setStrokeStyle(1, COLORS.windowBorder)
        .setInteractive({ useHandCursor: true });
      root.add(row);

      const label = this.scene.add.text(
        x + CASH_APP_MODAL.paddingX + 12,
        rowY + 12,
        summary.customer,
        {
          fontFamily: FONTS.ui,
          fontSize: CASH_APP_MODAL.customerFontSize,
          color: toHexColor(COLORS.textPrimary),
          fontStyle: 'bold',
        },
      );
      root.add(label);

      const meta = this.scene.add
        .text(
          x + CASH_APP_MODAL.paddingX + rowWidth - 12,
          rowY + 12,
          `${summary.openCount} open · ${formatDollars(summary.outstanding)}`,
          {
            fontFamily: FONTS.ui,
            fontSize: CASH_APP_MODAL.customerFontSize,
            color: toHexColor(COLORS.textMuted),
          },
        )
        .setOrigin(1, 0);
      root.add(meta);

      row.on('pointerdown', () => this.selectCustomer(summary.customer));
      rowY += CASH_APP_MODAL.customerRowHeight + CASH_APP_MODAL.customerRowGap;
    }

    const cancel = this.scene.add
      .text(
        x + CASH_APP_MODAL.width - CASH_APP_MODAL.paddingX,
        y + CASH_APP_MODAL.height - CASH_APP_MODAL.paddingY,
        COPY.cashApplication.cancelButton,
        {
          fontFamily: FONTS.ui,
          fontSize: CASH_APP_MODAL.backButtonFontSize,
          color: toHexColor(COLORS.textMuted),
        },
      )
      .setOrigin(1, 1)
      .setInteractive({ useHandCursor: true });
    cancel.on('pointerdown', () => this.close());
    root.add(cancel);
  }

  private selectCustomer(customer: string): void {
    this.selectedCustomer = customer;
    this.allocations.clear();
    this.render();
  }

  private renderAllocator(
    root: Phaser.GameObjects.Container,
    x: number,
    y: number,
    contentY: number,
  ): void {
    const customer = this.selectedCustomer!;

    const back = this.scene.add
      .text(x + CASH_APP_MODAL.paddingX, contentY, COPY.cashApplication.backButton, {
        fontFamily: FONTS.ui,
        fontSize: CASH_APP_MODAL.backButtonFontSize,
        color: toHexColor(COLORS.textMuted),
      })
      .setInteractive({ useHandCursor: true });
    back.on('pointerdown', () => {
      this.selectedCustomer = null;
      this.allocations.clear();
      this.render();
    });
    root.add(back);

    const prompt = this.scene.add.text(
      x + CASH_APP_MODAL.paddingX,
      contentY + back.height + 6,
      COPY.cashApplication.allocationPrompt(customer),
      {
        fontFamily: FONTS.ui,
        fontSize: CASH_APP_MODAL.promptFontSize,
        color: toHexColor(COLORS.textPrimary),
      },
    );
    root.add(prompt);

    const invoices = invoicesForCustomer(this.ledgerInvoices(), customer);
    const applied = [...this.allocations.values()].reduce((s, n) => s + n, 0);
    const wireAmount = this.currentPayment?.amount ?? 0;
    const unapplied = Math.max(0, wireAmount - applied);

    const counterBoxY =
      y +
      CASH_APP_MODAL.height -
      CASH_APP_MODAL.paddingY -
      CASH_APP_MODAL.counterBoxHeight -
      CASH_APP_MODAL.confirmButtonHeight -
      12;
    const counterBox = this.scene.add
      .rectangle(
        x + CASH_APP_MODAL.paddingX,
        counterBoxY,
        CASH_APP_MODAL.width - CASH_APP_MODAL.paddingX * 2,
        CASH_APP_MODAL.counterBoxHeight,
        COLORS.windowChrome,
      )
      .setOrigin(0, 0)
      .setStrokeStyle(1, COLORS.windowBorder);
    root.add(counterBox);

    const appliedText = this.scene.add.text(
      x + CASH_APP_MODAL.paddingX + 12,
      counterBoxY + 8,
      `${COPY.cashApplication.appliedLabel} ${formatDollars(applied)}`,
      {
        fontFamily: FONTS.ui,
        fontSize: CASH_APP_MODAL.counterFontSize,
        color: toHexColor(COLORS.textPrimary),
        fontStyle: 'bold',
      },
    );
    root.add(appliedText);

    const unappliedColor = unapplied === 0 ? COLORS.buttonPrimary : COLORS.accentWarn;
    const unappliedText = this.scene.add
      .text(
        x + CASH_APP_MODAL.paddingX + CASH_APP_MODAL.width - CASH_APP_MODAL.paddingX * 2 - 12,
        counterBoxY + 8,
        `${COPY.cashApplication.unappliedLabel} ${formatDollars(unapplied)}`,
        {
          fontFamily: FONTS.ui,
          fontSize: CASH_APP_MODAL.counterFontSize,
          color: toHexColor(unappliedColor),
          fontStyle: 'bold',
        },
      )
      .setOrigin(1, 0);
    root.add(unappliedText);

    const listTop = prompt.y + prompt.height + 10;
    const listBottom = counterBoxY - 8;
    let rowY = listTop;
    const rowWidth = CASH_APP_MODAL.width - CASH_APP_MODAL.paddingX * 2;

    for (const inv of invoices) {
      if (rowY + CASH_APP_MODAL.invoiceRowHeight > listBottom) break;
      const already = this.allocations.get(inv.id) ?? 0;
      const remainingOnInvoice = inv.amount - already;
      const allocatable = Math.min(unapplied, remainingOnInvoice);
      const canAllocate = allocatable > 0 && already === 0;
      const canRemove = already > 0;

      const row = this.scene.add
        .rectangle(
          x + CASH_APP_MODAL.paddingX,
          rowY,
          rowWidth,
          CASH_APP_MODAL.invoiceRowHeight,
          already > 0 ? COLORS.windowTitleBar : COLORS.windowChrome,
        )
        .setOrigin(0, 0)
        .setStrokeStyle(1, COLORS.windowBorder);
      root.add(row);

      const label = this.scene.add.text(
        x + CASH_APP_MODAL.paddingX + 12,
        rowY + 10,
        `${inv.invoiceNumber} · ${formatDollars(inv.amount)} · ${inv.agingDays}d`,
        {
          fontFamily: FONTS.ui,
          fontSize: CASH_APP_MODAL.invoiceFontSize,
          color: toHexColor(COLORS.textPrimary),
        },
      );
      root.add(label);

      const appliedTag = this.scene.add
        .text(
          x + CASH_APP_MODAL.paddingX + rowWidth - 120,
          rowY + 10,
          already > 0 ? `applied ${formatDollars(already)}` : '',
          {
            fontFamily: FONTS.ui,
            fontSize: CASH_APP_MODAL.invoiceFontSize,
            color: toHexColor(COLORS.accentWarn),
          },
        )
        .setOrigin(1, 0);
      root.add(appliedTag);

      const button = this.scene.add
        .rectangle(
          x + CASH_APP_MODAL.paddingX + rowWidth - 12,
          rowY + CASH_APP_MODAL.invoiceRowHeight / 2,
          80,
          CASH_APP_MODAL.invoiceRowHeight - 8,
          canAllocate || canRemove ? COLORS.buttonPrimary : COLORS.windowBorder,
        )
        .setOrigin(1, 0.5)
        .setInteractive({ useHandCursor: canAllocate || canRemove });
      root.add(button);

      const buttonText = this.scene.add
        .text(
          x + CASH_APP_MODAL.paddingX + rowWidth - 52,
          rowY + CASH_APP_MODAL.invoiceRowHeight / 2,
          canRemove ? COPY.cashApplication.removeButton : COPY.cashApplication.applyButton,
          {
            fontFamily: FONTS.ui,
            fontSize: CASH_APP_MODAL.invoiceFontSize,
            color: toHexColor(COLORS.buttonPrimaryText),
            fontStyle: 'bold',
          },
        )
        .setOrigin(0.5, 0.5);
      root.add(buttonText);

      if (canAllocate) {
        button.on('pointerdown', () => {
          this.allocations.set(inv.id, allocatable);
          this.render();
        });
      } else if (canRemove) {
        button.on('pointerdown', () => {
          this.allocations.delete(inv.id);
          this.render();
        });
      }

      rowY += CASH_APP_MODAL.invoiceRowHeight + CASH_APP_MODAL.invoiceRowGap;
    }

    const confirmEnabled = this.allocations.size > 0;
    const confirmY =
      y + CASH_APP_MODAL.height - CASH_APP_MODAL.paddingY - CASH_APP_MODAL.confirmButtonHeight;
    const confirm = this.scene.add
      .rectangle(
        x + CASH_APP_MODAL.paddingX + CASH_APP_MODAL.width - CASH_APP_MODAL.paddingX * 2 - 180,
        confirmY,
        180,
        CASH_APP_MODAL.confirmButtonHeight,
        confirmEnabled ? COLORS.buttonPrimary : COLORS.windowBorder,
      )
      .setOrigin(0, 0)
      .setInteractive({ useHandCursor: confirmEnabled });
    root.add(confirm);

    const confirmText = this.scene.add
      .text(
        x + CASH_APP_MODAL.paddingX + CASH_APP_MODAL.width - CASH_APP_MODAL.paddingX * 2 - 90,
        confirmY + CASH_APP_MODAL.confirmButtonHeight / 2,
        COPY.cashApplication.confirmButton,
        {
          fontFamily: FONTS.ui,
          fontSize: CASH_APP_MODAL.confirmButtonFontSize,
          color: toHexColor(COLORS.buttonPrimaryText),
          fontStyle: 'bold',
        },
      )
      .setOrigin(0.5, 0.5);
    root.add(confirmText);

    if (confirmEnabled) {
      confirm.on('pointerdown', () => {
        this.emitSent();
        this.showToast();
        this.close();
      });
    }

    const cancel = this.scene.add
      .text(
        x + CASH_APP_MODAL.paddingX,
        confirmY + CASH_APP_MODAL.confirmButtonHeight / 2,
        COPY.cashApplication.cancelButton,
        {
          fontFamily: FONTS.ui,
          fontSize: CASH_APP_MODAL.backButtonFontSize,
          color: toHexColor(COLORS.textMuted),
        },
      )
      .setOrigin(0, 0.5)
      .setInteractive({ useHandCursor: true });
    cancel.on('pointerdown', () => this.close());
    root.add(cancel);
  }

  private showToast(): void {
    const customer = this.selectedCustomer ?? '';
    const toast = this.scene.add
      .text(
        GAME_WIDTH / 2,
        GAME_HEIGHT - 80,
        `${COPY.cashApplication.sentToastPrefix} ${customer}`,
        {
          fontFamily: FONTS.ui,
          fontSize: CASH_APP_MODAL.toastFontSize,
          color: toHexColor(COLORS.textPrimary),
          backgroundColor: toHexColor(COLORS.windowChrome),
          padding: { x: 12, y: 8 },
        },
      )
      .setOrigin(0.5, 0.5)
      .setDepth(DEPTHS.modal);

    this.scene.time.delayedCall(CASH_APP_MODAL.toastDurationMs, () => toast.destroy());
  }

  private close(): void {
    if (this.escHandler) {
      this.scene.input.keyboard?.off('keydown', this.escHandler);
      this.escHandler = null;
    }
    if (this.container) {
      this.container.destroy();
      this.container = null;
    }
    if (this.currentPayment) {
      this.bus.emit('cash-application:closed', { wireId: this.currentPayment.id });
      this.currentPayment = null;
    }
    this.selectedCustomer = null;
    this.allocations.clear();
  }

  private teardown(): void {
    this.bus.off('cash-application:open-for', this.onOpenFor);
    this.close();
  }
}
